import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import usePaymentDeviceRequestsPendingCount from '@/hooks/usePaymentDeviceRequestsPendingCount';
import { PaymentDeviceSummaryDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { MANAGEMENT_DEVICES_URL } from '@/helpers/paymentDevices';
import InventoryTab from './InventoryTab';
import RequestsTab, { RequestsTabKind } from './RequestsTab';
import FeesTab from './FeesTab';

type MainTab = 'inventory' | 'requests' | 'fees';

/**
 * «מכשירי סליקה» (HAT-1434): the office's inventory of rentable card terminals, and the rental
 * requests coordinators send for them. Two main tabs, so the inventory is never mistaken for one
 * of the request statuses; the statuses are sub-tabs inside «בקשות השכרה». A third, «עמלות»,
 * appears once the rental fee (part 3) is on for the installation.
 */
const PaymentDevicesManagementPage = () => {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState<MainTab>('inventory');
  const [requestsTab, setRequestsTab] = useState<RequestsTabKind>('pending');
  const [openRequestId, setOpenRequestId] = useState<number | null>(null);
  const pendingCount = usePaymentDeviceRequestsPendingCount();
  // The same query the inventory's counters read, so this costs no extra request.
  const { data: summary } = useGetAuthApi<PaymentDeviceSummaryDtoIn>(`${MANAGEMENT_DEVICES_URL}/summary`);
  const feesEnabled = !!summary?.feesEnabled;
  const shownTab: MainTab = mainTab === 'fees' && !feesEnabled ? 'inventory' : mainTab;

  // A request holding a device, opened from the inventory or the approve dialog: it is in «אושרו».
  const openHeldRequest = (requestId: number) => {
    setOpenRequestId(requestId);
    setRequestsTab('approved');
    setMainTab('requests');
  };
  const switchRequestsTab = (tab: RequestsTabKind) => {
    setOpenRequestId(null);
    setRequestsTab(tab);
  };

  return (
    <div>
      <Tabs value={shownTab} onValueChange={(value) => setMainTab(value as MainTab)} style={{ direction: 'rtl' }} className="mb-6">
        <TabsList>
          <TabsTrigger value="inventory">{t('paymentDevices.tabs.inventory')}</TabsTrigger>
          <TabsTrigger value="requests">
            {pendingCount ? t('paymentDevices.tabs.requestsWithCount', { count: pendingCount }) : t('paymentDevices.tabs.requests')}
          </TabsTrigger>
          {feesEnabled && <TabsTrigger value="fees">{t('paymentDevices.tabs.fees')}</TabsTrigger>}
        </TabsList>
      </Tabs>
      {shownTab === 'inventory' && <InventoryTab onOpenRequest={openHeldRequest} />}
      {shownTab === 'requests' && (
        <>
          <Tabs value={requestsTab} onValueChange={(value) => switchRequestsTab(value as RequestsTabKind)} style={{ direction: 'rtl' }} className="mb-6">
            <TabsList variant="pills">
              <TabsTrigger variant="pills" value="pending">
                {pendingCount ? t('paymentDevices.tabs.pendingWithCount', { count: pendingCount }) : t('paymentDevices.tabs.pending')}
              </TabsTrigger>
              <TabsTrigger variant="pills" value="approved">{t('paymentDevices.tabs.approved')}</TabsTrigger>
              <TabsTrigger variant="pills" value="closed">{t('paymentDevices.tabs.closed')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <RequestsTab
            key={`${requestsTab}-${openRequestId ?? ''}`}
            kind={requestsTab}
            initialRequestId={openRequestId}
            onOpenRequest={openHeldRequest}
          />
        </>
      )}
      {shownTab === 'fees' && <FeesTab />}
    </div>
  );
};

export default PaymentDevicesManagementPage;
